"use client";
import { Text } from "@dxp-feed/ui/Text";
import BreadcrumbWrapper from "@/components/Breadcrumb";
import { Box } from "@dxp-feed/ui/Box";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { SideBarTemplateProps } from "@/utils/types/recipes";
import { SiteContainer } from "@/components/Container";
import Sort from "@/components/Filters/Sort";
import Filter from "@/components/Filters/Filter";
import { setPagecount } from "@/utils/redux/common/pagination";
import AlgoliaPagination from "@/templates/algolia/Pagination";
import { Configure } from "@dxp-feed/algolia";
import Category from "@/components/Filters/Category";
import AlgoliaMiddleware from "@/templates/algolia/Middleware";
import { algoliaConfig } from "@/services/algolia/config";
import AdSlot from "@/helpers/gpt";
import { adsScript } from "@/utils/adsTempConstant";
import Loader from "@/components/Loader";
import { setAlgoliaLoading } from "@/utils/redux/recipes/algoliaSlice";
import { setFilters } from "@/utils/redux/productsListing";
import { setCurrentPage } from "@/utils/redux/ads";
import { useSearchParams } from "next/navigation";
import { useRefine } from "@/templates/provider/RefineContext";
import parse from "html-react-parser";
import DietaryPreferenceCarousel from "@/templates/home/_components/DietaryPreferencesCarousel";
import useScrollBlocker from "@/templates/recipes/common/commonRecipeFunctions/useScrollBlocker";
import { RESET_FILTERS } from "@/utils/constant";
import { setFavouriteMessage } from "@/utils/redux/favouriteProduct";
import { setShoppingMessage } from "@/utils/redux/shoppingList";

const SideBarTemplate: React.FC<SideBarTemplateProps> = ({
  breadcrumbList,
  filterData,
  children,
  isCatVisible = false,
  isUlVisible = false,
  isSortVisible = false,
  closeUl,
  closeSort,
  closeCat,
  isSearchPage = false,
  type,
  clearFilterAttributes,
  filters,
  selectedCategory,
  isBrandPage,
  className = "",
  searchQuery,
  adSlots = true,
  showSideBar = true,
  pagePath,
  setSortOption,
  showPagination = true,
  initialCategory,
  // optionalFilters
  displayedOffers,
  preferredResults,
}) => {
  const { bodyRef, disableBackgroundScroll, enableBackgroundScroll } =
    useScrollBlocker();
  const path = useAppSelector((state) => state?.adsSclice?.page ?? "");
  const { pagecount } = useAppSelector((state) => state.paginationSlice);
  const dispatch: any = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const { uid, loggedIn, lifestyles } = useAppSelector(
    (state) => state.gigyaslice
  );
  const searchParams = useSearchParams();
  const itemsPerPageQuery: any = searchParams.get("itemsPerPage");
  const perPage: number = parseInt(itemsPerPageQuery ?? pagecount);
  const [PagecountDefault, setPagecountDefault] = useState(perPage);
  const userId = uid ?? undefined;
  const { ProductAvailabilityMessage, Banner } = useAppSelector(
    (state) => state.getProductAemDataSlice
  );
  const [resetSortTrigger, setResetSortTrigger] = useState(false);

  useEffect(() => {
    if (resetSortTrigger) {
      setResetSortTrigger(false);
    }
  }, [resetSortTrigger]);

  const handleClearAllFilters = () => {
    setResetSortTrigger(true);
  };

  const handleChange = (value: string) => {
    setPagecountDefault(parseInt(value));
    dispatch(setPagecount(parseInt(value)));
    dispatch(setFavouriteMessage());
    dispatch(setShoppingMessage());
  };
  const indexName = algoliaConfig.indexNames[type];
  useEffect(() => {
    setPagecountDefault(perPage);
  }, [perPage, searchParams]);
  useEffect(() => {
    dispatch(setAlgoliaLoading(loading));
  }, [loading, dispatch]);

  const tab = searchParams.get("tab");
  // Disable background only for Recipe page stash
  useEffect(() => {
    if (pagePath === "recipes" && !isSearchPage) {
      if (loading) {
        disableBackgroundScroll();
      } else {
        enableBackgroundScroll();
      }
    } else {
      enableBackgroundScroll();
    }
    return () => enableBackgroundScroll();
  }, [
    pagePath,
    loading,
    isSearchPage,
    tab,
    disableBackgroundScroll,
    enableBackgroundScroll,
  ]);

  const sanitizedBreadcrumbList = breadcrumbList.map((item) => ({
    ...item,
    href: item.href ?? null,
  }));
  useEffect(() => {
    let page;
    if (pagePath === "flyer") {
      page = "/flyer/product";
    } else if (isSearchPage) {
      page = "/search";
    } else if (pagePath === "products") {
      page = "/products";
    } else if (pagePath === "articles") {
      page = "/articles";
    } else if (pagePath === "recipes") {
      page = "/recipes";
    }
    if (!isBrandPage) {
      dispatch(setCurrentPage(page));
    }
  }, [isSearchPage, isBrandPage]);

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const { setRefineFunctions } = useRefine();

  useEffect(() => {
    setAppliedFilters(filters);
  }, [filters]);
  const handleFiltersUpdate = useCallback(
    (newFilter: any) => {
      let updatedFilters = filters;
      // For resetting the filter when Clear All Button is Clicked
      if (newFilter == RESET_FILTERS) {
        updatedFilters = filters;
      }
      // For appending the filter when checkbox is checked
      else if (newFilter) {
        updatedFilters = filters ? `${filters} AND ${newFilter}` : filters;
      } else {
        // const filterRegex = /AND promotions\.startDate<=\d+ AND promotions\.endDate>=\d+/;
        const filterRegex =
          /AND promotions\.promotionComplianceFlag:true AND promotions\.startDate<=\d+ AND promotions\.endDate>=\d+ AND promotions\.source:[^\s]+/;

        // Remove the flyer filter when checkbox is unchecked
        updatedFilters = filters?.replace(filterRegex, "").trim();
      }
      dispatch(
        setFilters({
          filter: "flyer",
          filterName: "Show Flyers Only",
          filterValue: "",
        })
      );
      setAppliedFilters(updatedFilters);
    },
    [filters]
  );

  useEffect(() => {
    setRefineFunctions((prev) => ({ ...prev, flyer: handleFiltersUpdate }));
  }, [handleFiltersUpdate]);

  const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    const width = window.innerWidth;
    let selectedImage = Banner?.mobileimage;

    if (width >= 1024) {
      selectedImage = Banner?.image; // Desktop
    } else if (width >= 768) {
      selectedImage = Banner?.tabletimage; // Tablet
    }

    setImageSrc(`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${selectedImage}`);
  }, []);
  return (
    <Box as="div" className={className} ref={bodyRef}>
      {!isSearchPage && (
        <SiteContainer>
          {!isBrandPage && (
            <BreadcrumbWrapper breadcrumbList={sanitizedBreadcrumbList} />
          )}
          <AlgoliaMiddleware
            userId={userId}
            indexes={[
              {
                label: type,
                name: indexName,
              },
            ]}
            pageType="LISTING"
            setLoading={setLoading}
          />
          {!isSearchPage && adSlots && (
            <Box
              as="div"
              className="flex items-center justify-center lg:gap-[24px]"
            >
              <Box as="div" className="lg:w-[250px] xl:w-[220px]"></Box>
              <Box
                as="div"
                className={`w-full lg:w-[calc(100%-250px)] xl:w-[calc(100%-220px)] flex items-center justify-center flex-col`}
              >
                <AdSlot
                  id={adsScript?.[path]?.leaderboardTop_A?.id}
                  className="max-w-[320px] md:max-w-[728px] lg:max-w-[970px] m-auto [&_iframe]:m-auto pb-4 xl:pb-0"
                />

                <AdSlot
                  id={adsScript?.[path]?.leaderboardTop_B?.id}
                  className="xl:flex hidden mt-8 items-center justify-center pb-6"
                />
              </Box>
            </Box>
          )}
        </SiteContainer>
      )}
      {PagecountDefault > 0 && (
        <Configure
          hitsPerPage={PagecountDefault}
          filters={type == "products" ? appliedFilters : filters}
          // optionalFilters={type === "products" ? optionalFilters : []}
        />
      )}
      {loading && !isSearchPage ? (
        <Loader className="fixed inset-0 bg-[#fff] bg-opacity-70 z-[9999] flex items-center justify-center" />
      ) : null}

      <SiteContainer className="main-wrapper lg:mt-6">
      <Text as="h1" className="sr-only">Page Main Heading</Text>
        <Box as="div" className="flex lg:gap-[24px]">
          <aside className="lg:w-[250px] xl:w-[220px] mb-[38px] sideBar">
            <Text
              as="div"
              className={`${!showSideBar ? "no-product flex flex-col gap-4 lg:w-[250px] xl:w-[220px]" : "flex flex-col gap-4 lg:w-[250px] xl:w-[220px]"}`}
            >
              <Sort
                isSortVisible={isSortVisible}
                type={type}
                closeSort={closeSort}
                sortClear={resetSortTrigger}
                setSortOption={setSortOption}
                displayedOffers={displayedOffers}
              />
              {["products", "articles"].includes(type) && (
                <Category
                  isCatVisible={isCatVisible}
                  type={type}
                  closeCat={closeCat}
                  selectedCategory={selectedCategory}
                  isSearchPage={isSearchPage}
                />
              )}
              {type !== "articles" ? (
                <Filter
                  isUlVisible={isUlVisible}
                  closeUl={closeUl}
                  filterData={filterData.length ? filterData : []}
                  type={type}
                  clearFilterAttributes={clearFilterAttributes}
                  onFiltersUpdate={handleFiltersUpdate}
                  onClearAllFilters={handleClearAllFilters}
                  isSearchPage={isSearchPage}
                />
              ) : null}

              {/* {!isSearchPage && ( */}
              <Box
                as="div"
                className="max-w-[220px] m-auto hidden lg:flex  flex-col  gap-4"
              >
                {["products", "recipes"].includes(type) && adSlots && (
                  <>
                    <AdSlot id={adsScript?.[path]?.rectangleMiddle_A?.id} />

                    <AdSlot id={adsScript?.[path]?.rectangleMiddle_B?.id} />
                  </>
                )}
              </Box>
            </Text>
          </aside>
          <main
            className={
              isSearchPage
                ? `pb-[24px] md:pb-6 lg:pb-12 w-full lg:${searchQuery ? `w-[calc(100%-250px)]` : "w-full"} xl:${searchQuery ? `w-[calc(100%-220px)]` : `w-full`}`
                : `pb-8 md:pb-6 lg:pb-12 w-full ${showSideBar ? "lg:w-[calc(100%-250px)] xl:w-[calc(100%-220px)]" : ""} `
            }
          >
            <Box as="div" className="grid-outer">
              {children}
            </Box>

            {!!searchQuery === isSearchPage && showPagination && (
              <AlgoliaPagination
                type={type}
                itemPerPage={PagecountDefault}
                changeItemPerPage={handleChange}
              />
            )}
            {isSearchPage && searchQuery && loggedIn && preferredResults && (
              <DietaryPreferenceCarousel
                type={type}
                preferredResults={preferredResults}
              />
            )}

            {type === "products" && (
              <section className="products-p pt-6">
                <Box as="div" className="!text-grey900 text-body text-center">
                  {ProductAvailabilityMessage &&
                  ProductAvailabilityMessage.length > 0
                    ? parse(ProductAvailabilityMessage)
                    : "Product availability may vary. See in-store for accurate product availability."}
                </Box>
              </section>
            )}
            {/* {!isSearchPage && ( */}
            
              {path != "/search" &&
                ["products", "recipes"].includes(type) &&
                adSlots && (
                  <Box
                  as="div"
                  className="max-w-full m-auto [&_iframe]:m-auto overflow-hidden flex items-center justify-center flex-wrap mt-6"
                >
                  <AdSlot
                    id={adsScript?.[path]?.leaderboardBottom_A?.id}
                    className=" gap-6 m-auto mt-6"
                  />
                  </Box>
                )}
              {path !== "/products" &&
                ["products", "recipes"].includes(type) && (
                  <Box
                  as="div"
                  className="max-w-full m-auto [&_iframe]:m-auto overflow-hidden flex items-center justify-center flex-wrap mt-6"
                >
                  <Box as="div" className="xl:flex hidden">
                    <AdSlot id={adsScript?.[path]?.leaderboardBottom_B?.id} />
                  </Box>
                </Box>
                )}
          </main>
        </Box>
      </SiteContainer>
    </Box>
  );
};

export default SideBarTemplate;
